import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Ellipsis, Plus, Trash2 } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { godMeQuery, godsQuery, lookupForGod, meQuery, useGrantGod, useRevokeGod } from '@/api/queries'
import type { God } from '@/api/types'
import { EmailLookup, type Found } from '@/app/email-lookup'
import { NotForYou } from '@/app/not-for-you'
import { errorText } from '@/domain/errors'
import { formatDate, t } from '@/i18n'
import { Button } from '@/ui/button'
import { Avatar } from '@/ui/compact-row'
import { DataList, type DataColumn } from '@/ui/data-list'
import { InlineBanner } from '@/ui/inline-banner'
import { ConfirmDialog, RowMenu, RowMenuItem, SidePanel } from '@/ui/overlays'
import { Page, PageHeader } from '@/ui/page'
import { LoadingBlock } from '@/ui/spinner'

export const Route = createFileRoute('/_app/gods/list')({
  component: Gods,
})

const godName = (god: God): string => god.name || god.email || t.gods.unknownName

/// Name (+«Ви»), email, since when — and who made them: a bootstrap god has
/// no granter and says so.
const godColumns = (me: string | null): readonly DataColumn<God>[] => [
  {
    key: 'name',
    header: t.gods.columns.name,
    width: 'minmax(0,1.3fr)',
    cell: (god) => (
      <span className="flex min-w-0 items-center gap-2.5">
        <Avatar name={godName(god)} className="size-7 text-[11px] max-sm:hidden" />
        <span className="truncate">{godName(god)}</span>
        {god.userId === me ? (
          <span className="shrink-0 rounded-full px-1.5 py-px text-footnote font-medium text-text-2 tint-active">{t.app.you}</span>
        ) : null}
      </span>
    ),
  },
  {
    key: 'email',
    header: t.gods.columns.email,
    width: 'minmax(0,1.3fr)',
    cell: (god) => (god.name && god.email ? god.email : <span className="text-muted">—</span>),
  },
  {
    key: 'granted',
    header: t.gods.columns.granted,
    width: 'minmax(0,1fr)',
    mobile: 'hidden',
    hideBelow: 'md',
    cell: (god) => (god.grantedBy ? formatDate(god.grantedAt) : t.gods.bootstrap),
  },
]

/// Gods' console · Боги: eatlete staff with power over every company. Only a
/// god adds or removes one (G4); the last one stays (G5, 409 last_god).
function Gods() {
  const isGod = useQuery(godMeQuery())
  const gods = useQuery(godsQuery())
  const me = useQuery(meQuery())
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<God | null>(null)
  const revoke = useRevokeGod()

  if (isGod.data === false) return <NotForYou />

  return (
    <Page wide>
      <PageHeader
        title={t.gods.title}
        meta={gods.data ? String(gods.data.length) : undefined}
        subtitle={t.gods.subtitle}
        actions={
          <Button variant="primary" icon={<Plus className="size-[18px]" strokeWidth={2.5} />} onClick={() => setAdding(true)}>
            {t.gods.add}
          </Button>
        }
      />

      {gods.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : gods.isError ? (
        <InlineBanner
          kind="error"
          message={errorText(gods.error)}
          action={{ label: t.app.retry, onClick: () => void gods.refetch() }}
        />
      ) : (
        <DataList
          label={t.gods.title}
          columns={godColumns(me.data?.id ?? null)}
          rows={gods.data}
          rowKey={(god) => god.userId}
          rowActions={(god) => (
            <RowMenu label={`${t.app.more}: ${godName(god)}`} trigger={<Ellipsis className="size-[18px]" />}>
              <RowMenuItem destructive icon={<Trash2 className="size-4" />} onSelect={() => setRemoving(god)}>
                {t.gods.remove}
              </RowMenuItem>
            </RowMenu>
          )}
        />
      )}

      <AddGodPanel open={adding} onOpenChange={setAdding} />

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRemoving(null)
            revoke.reset()
          }
        }}
        title={removing ? t.gods.removeTitle(godName(removing)) : ''}
        message={removing?.userId === me.data?.id ? t.gods.removeSelfMessage : t.gods.removeMessage}
        confirmLabel={t.gods.removeConfirm}
        busy={revoke.isPending}
        error={revoke.isError ? <InlineBanner kind="error" message={errorText(revoke.error)} /> : null}
        onConfirm={() => {
          if (!removing) return
          const self = removing.userId === me.data?.id
          revoke.mutate(removing.userId, {
            onSuccess: () => {
              setRemoving(null)
              // No longer a god: the console is gone for us.
              if (self) window.location.assign('/')
            },
          })
        }}
      />
    </Page>
  )
}

function AddGodPanel({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const grant = useGrantGod()
  const [found, setFound] = useState<Found | null>(null)
  const [missing, setMissing] = useState(false)

  const close = () => {
    onOpenChange(false)
    setFound(null)
    setMissing(false)
    grant.reset()
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!found || found.blocked) {
      setMissing(!found)
      return
    }
    grant.mutate(found.person.id, { onSuccess: close })
  }

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title={t.gods.form.title}
      description={t.gods.form.hint}
      onSubmit={submit}
      footer={
        <>
          <Button variant="quiet" onClick={close}>
            {t.app.cancel}
          </Button>
          <Button type="submit" variant="primary" busy={grant.isPending} disabled={found?.blocked === true}>
            {grant.isPending ? t.gods.form.submitting : t.gods.form.submit}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <EmailLookup
          autoFocus
          label={t.gods.form.email}
          found={found}
          onFound={(next) => {
            setFound(next)
            setMissing(false)
            grant.reset()
          }}
          error={missing ? t.staff.form.personRequired : undefined}
          lookup={async (email) => {
            const res = await lookupForGod(email)
            return res.god
              ? { person: res.user, note: t.lookup.alreadyGod, blocked: true }
              : { person: res.user }
          }}
        />
        {grant.isError ? <InlineBanner kind="error" message={errorText(grant.error)} /> : null}
      </div>
    </SidePanel>
  )
}
